import React, { useState, useEffect } from 'react';
import { Container, TextField, Button, Grid, Typography, Box, Select, MenuItem, InputLabel, FormControl, FormHelperText, Alert, AlertTitle } from '@mui/material';
import { GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector, GridToolbarExport, useGridApiContext } from '@mui/x-data-grid';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { darken, lighten, styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';

const CustomToolbar = () => {
  const apiRef = useGridApiContext();

  const handleReset = () => {
    apiRef.current.setFilterModel({ items: [] });
    apiRef.current.setSortModel([]);
    apiRef.current.setColumnVisibilityModel({});
    apiRef.current.setDensity('standard');
  };

  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <Button
        size="small"
        onClick={handleReset}
        startIcon={<RefreshIcon fontSize="medium" />}
        sx={{
          fontSize: '14px',
          textTransform: 'uppercase',
          padding: '4px 8px',
          minWidth: 'auto',
          '& .MuiButton-startIcon': {
            marginRight: '4px',
            '& > *:nth-of-type(1)': {
              fontSize: '20px',
            },
          },
        }}
      >
        Reset
      </Button>
    </GridToolbarContainer>
  );
};

const DriverForm = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // State management
  const [driversList, setDriversList] = useState([]);
  const [unitNumbers, setUnitNumbers] = useState([]);
  const [cardsForClient, setCardsForClient] = useState([]);
  const [clients, setClients] = useState([]);
  const [licenseFiles, setLicenseFiles] = useState([]);
  const [isAddDriverSelected, setIsAddDriverSelected] = useState(false);
  const [isUpdateDriverSelected, setIsUpdateDriverSelected] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [licenseState, setLicenseState] = useState('');
  const [customerAccount, setCustomerAccount] = useState('');

  // Form states
  const [driverId, setDriverId] = useState('');
  const [driverName, setDriverName] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseNumberError, setLicenseNumberError] = useState('');
  const [primaryPhone, setPrimaryPhone] = useState('+1');
  const [secondaryPhone, setSecondaryPhone] = useState('+1');
  const [unitNumber, setUnitNumber] = useState('');
  const [driverStatus, setDriverStatus] = useState('Inactive');
  const [assignedCard, setAssignedCard] = useState('');
  const [licenseFile, setLicenseFile] = useState(null);

  // Role-based filtration
  const loggedInUserClientId = localStorage.getItem('clientId');
  const loggedInUserName = localStorage.getItem('userName');
  const doNotPerformCustomerFiltration = localStorage.getItem('role') === 'Super Admin'|| localStorage.getItem('role') === 'Employee';

  useEffect(() => {
    setCustomerAccount(`${loggedInUserClientId}-${loggedInUserName}`);
  }, [loggedInUserClientId, loggedInUserName]);

  const driverStatuses = [
    { id: 'Active', value: 'Active' },
    { id: 'Inactive', value: 'Inactive' },
  ];

  const licenseStates = [
    { id: 'ON', value: 'ON' },
    { id: 'QC', value: 'QC' },
    { id: 'NB', value: 'NB' },
    { id: 'NS', value: 'NS' },
    { id: 'NFL', value: 'NFL' },
    { id: 'MB', value: 'MB' },
    { id: 'SK', value: 'SK' },
    { id: 'AB', value: 'AB' },
    { id: 'BC', value: 'BC' },
    { id: 'AK', value: 'AK' },
    { id: 'YK', value: 'YK' },
    { id: 'NT', value: 'NT' },
    { id: 'NU', value: 'NU' },
  ];

  useEffect(() => {
    console.log('Starting initial data fetch for customerAccount:', customerAccount);
    Promise.all([getClients(), fetchDrivers(), fetchUnits(), fetchCards(), fetchLicenseFiles()])
      .then(([clientsData, driversData, unitsData, cardsData, licenseFilesData]) => {
        console.log('All data fetched successfully');
        generateListViewDetails(driversData, licenseFilesData);
        setUnitNumbers(unitsData.map(unit => unit.unitNumber));
      })
      .catch(error => {
        console.error('Error during initial fetch:', error);
        setErrorMessage('Failed to load data: ' + error.message);
        setIsError(true);
      });
  }, [customerAccount]);

  const getClients = async () => {
    let getClientsUrl = `${BACKEND_URL}/v1/client`;
    if (!doNotPerformCustomerFiltration) {
      getClientsUrl += `?clientID=${loggedInUserClientId}`;
    }
    const response = await axios.get(getClientsUrl);
    const data = response.data.sort((a, b) => a.clientID - b.clientID);
    console.log('Clients fetched:', data);
    setClients(data);
    return data;
  };

  const fetchCards = async () => {
    const response = await axios.get(`${BACKEND_URL}/v1/card/all`);
    const inactiveCards = response.data.filter(card => card.status === 'INACTIVE');
    console.log('Cards fetched:', inactiveCards);
    setCardsForClient(inactiveCards);
    return inactiveCards;
  };

  const fetchUnits = async () => {
    const response = await axios.get(`${BACKEND_URL}/v1/unit`);
    console.log('Units fetched:', response.data);
    return response.data;
  };

  const fetchDrivers = async () => {
    const response = await axios.get(`${BACKEND_URL}/v1/drivers`);
    console.log('Raw drivers fetched:', response.data);
    return response.data;
  };

  const fetchLicenseFiles = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/v1/get-all-driver-license-files`);
      console.log('License files fetched:', response.data);
      setLicenseFiles(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching license files:', error);
      return [];
    }
  };

  const generateListViewDetails = (drivers, licenseFilesData = []) => {
    console.log('Generating list view with drivers:', drivers, 'and license files:', licenseFilesData);
    console.log('Current customerAccount:', customerAccount);
    if (!drivers || drivers.length === 0) {
      console.log('No drivers to display');
      setDriversList([]);
      return;
    }

    // Filter drivers by customerAccount for non-Super Admins
    const filteredDrivers = doNotPerformCustomerFiltration
      ? drivers
      : drivers.filter(driver => {
          const matches = driver.customerAccount === customerAccount;
          console.log(`Driver ${driver.driverId}: customerAccount "${driver.customerAccount}" vs "${customerAccount}" -> ${matches}`);
          return matches;
        });

    console.log('Filtered drivers:', filteredDrivers);

    const driverListViewDetails = filteredDrivers.map(driver => {
      const licenseFileMatch = licenseFilesData.find(file => file.fileName === driver.licenseFileName) || {};
      // Find the client details from the clients state
      const client = clients.find(c => c.clientID === driver.customerId) || {};
      const customerAccountFallback = client.clientID ? `${client.clientID}-${client.clientName || 'Unknown'}` : `${loggedInUserClientId}-${loggedInUserName}`; // Fallback to logged-in user details

      const transformedDriver = {
        id: driver.driverId,
        driverId: driver.driverId || 'N/A',
        driverName: driver.driverName || '',
        licenseState: driver.licenseState || '',
        licenseNumber: driver.licenseNumber || '',
        primaryPhone: driver.primaryPhone || '',
        secondaryPhone: driver.secondaryPhone || '',
        unitNumber: driver.unitNumber || '',
        driverStatus: driver.driverStatus ? 'ACTIVE' : 'INACTIVE',
        assignedCard: driver.assignedCard || '',
        licenseFileName: licenseFileMatch.fileName || driver.licenseFileName || '',
        licenseFileUrl: licenseFileMatch.fileUrl || driver.licenseDocumentUrl || '',
        customerAccount: driver.customerAccount || customerAccountFallback, // Prioritize backend value
      };
      console.log(`Transformed driver ${driver.driverId}:`, transformedDriver);
      return transformedDriver;
    });

    console.log('Final drivers list for table:', driverListViewDetails);
    setDriversList(driverListViewDetails);
  };

  const checkLicenseNumberExistence = async licenseNum => {
    try {
      const response = await axios.get(`${BACKEND_URL}/v1/driver/license/${licenseNum}`);
      if (response.status === 200 && response.data) {
        setLicenseNumberError('License Number already exists');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setLicenseNumberError('');
      } else {
        // setLicenseNumberError("Error checking license number");
      }
    }
  };

  const handleLicenseNumberChange = event => {
    const value = event.target.value;
    setLicenseNumber(value);
    setLicenseNumberError('');
    checkLicenseNumberExistence(value);
  };

  const resetFields = () => {
    setDriverId('');
    setDriverName('');
    setLicenseNumber('');
    setLicenseNumberError('');
    setPrimaryPhone('+1');
    setSecondaryPhone('+1');
    setUnitNumber('');
    setDriverStatus('Inactive');
    setAssignedCard('');
    setLicenseFile(null);
    setLicenseState('');
  };

  const handleCreateDriver = async event => {
    event.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setErrorMessage(Object.values(errors).join(', '));
      setIsError(true);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('driverId', driverId);
      formData.append('driverName', driverName);
      formData.append('licenseNumber', licenseNumber);
      formData.append('primaryPhone', primaryPhone);
      formData.append('secondaryPhone', secondaryPhone);
      formData.append('unitNumber', unitNumber);
      formData.append('driverStatus', driverStatus === 'Active');
      formData.append('assignedCard', assignedCard);
      formData.append('customerAccount', customerAccount);
      formData.append('licenseState', licenseState);

      if (licenseFile) {
        console.log('Sending licenseFileName:', licenseFile.name);
        formData.append('licenseDocument', licenseFile);
        formData.append('licenseFileName', licenseFile.name);
      } else {
        formData.append('licenseFileName', '');
      }

      await axios.post(`${BACKEND_URL}/v1/driver`, formData);

      // Update success state
      setSuccessMessage('Driver created successfully!');
      setIsSuccess(true);
      setIsError(false);

      // Refresh driver list and reset form
      const [driversData, licenseFilesData] = await Promise.all([fetchDrivers(), fetchLicenseFiles()]);
      generateListViewDetails(driversData, licenseFilesData);
      resetFields();

      // Delay page reload to show the success alert
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      setErrorMessage('Failed to create driver: ' + (error.response?.data?.message || error.message));
      setIsError(true);
      setIsSuccess(false);
    }
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (file && file.size <= 100 * 1024 * 1024) {
      setLicenseFile(file);
    } else {
      setErrorMessage('File size exceeds 100 MB limit');
      setIsError(true);
    }
  };

  const handleUpdateDriver = async event => {
    event.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setErrorMessage(Object.values(errors).join(', '));
      setIsError(true);
      return;
    }

    try {
      const formData = new FormData();
      const driverData = {
        driverId: driverId,
        driverName: driverName,
        licenseNumber: licenseNumber,
        primaryPhone: primaryPhone,
        secondaryPhone: secondaryPhone,
        unitNumber: unitNumber,
        driverStatus: driverStatus === 'Active',
        assignedCard: assignedCard,
        customerAccount: customerAccount,
        licenseState: licenseState,
      };
      formData.append('driver', new Blob([JSON.stringify(driverData)], { type: 'application/json' }));

      if (licenseFile) {
        console.log('Sending licenseFileName:', licenseFile.name);
        formData.append('licenseFile', licenseFile);
      }

      const response = await axios.put(`${BACKEND_URL}/v1/driver/${driverId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSuccessMessage('Driver updated successfully!');
      setIsSuccess(true);
      setIsError(false);

      const [driversData, licenseFilesData] = await Promise.all([fetchDrivers(), fetchLicenseFiles()]);
      generateListViewDetails(driversData, licenseFilesData);
      resetFields();

      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Error updating driver:', error.response?.data || error.message);
      setErrorMessage('Failed to update driver: ' + (error.response?.data?.message || error.message));
      setIsError(true);
      setIsSuccess(false);
    }
  };

  const handleUpdateDriverSelectionChange = event => {
    const selectedDriverId = event.target.value;
    setDriverId(selectedDriverId);
    const driver = driversList.find(d => d.driverId === selectedDriverId);
    if (driver) {
      setDriverName(driver.driverName);
      setLicenseNumber(driver.licenseNumber);
      setPrimaryPhone(driver.primaryPhone || '+1');
      setSecondaryPhone(driver.secondaryPhone || '+1');
      setUnitNumber(driver.unitNumber);
      setDriverStatus(driver.driverStatus === 'ACTIVE' ? 'Active' : 'Inactive');
      setAssignedCard(driver.assignedCard);
      setLicenseState(driver.licenseState || '');
      setCustomerAccount(driver.customerAccount || `${loggedInUserClientId}-${loggedInUserName}`);
    }
  };

  const handleDelete = async driverId => {
    const confirmation = window.confirm(`Are you sure you want to delete driver: ${driverId}?`);
    if (confirmation) {
      try {
        const response = await axios.post(`${BACKEND_URL}/v1/send-driver-deletion-approval-email?driverId=${driverId}`);
        setSuccessMessage(response.data.message || 'Approval email sent successfully!');
        setIsSuccess(true);
        setIsError(false);
        window.location.reload();
      } catch (error) {
        setErrorMessage('Failed to send approval email: ' + error.message);
        setIsError(true);
        setIsSuccess(false);
      }
    }
  };

  const handleDownload = async fileName => {
    if (!fileName) {
      console.error('No file name provided for download');
      setErrorMessage('No license file available for download');
      setIsError(true);
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/v1/get-driver-license-file?fileName=${fileName}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: response.headers['content-type'],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`Successfully triggered download for: ${fileName}`);
    } catch (error) {
      console.error('Error downloading file:', error);
      setErrorMessage('Failed to download license file: ' + (error.response?.data?.message || error.message));
      setIsError(true);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!driverId) errors.driverId = 'Driver ID is required';
    if (!driverName) errors.driverName = 'Driver Name is required';
    if (!licenseNumber) errors.licenseNumber = 'License Number is required';
    if (!licenseState) errors.licenseState = 'License State is required';
    if (!primaryPhone) errors.primaryPhone = 'Primary Phone is required';
    return errors;
  };

  const getHoverBackgroundColor = (color, mode) => (mode === 'dark' ? darken(color, 0.6) : lighten(color, 0.6));

  const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    '& .MuiDataGrid-cell:hover': {
      cursor: 'pointer',
    },
    '& .even': {
      backgroundColor: 'rgb(248, 248, 248)',
      '&:hover': {
        backgroundColor: getHoverBackgroundColor('rgb(248, 248, 248)', theme.palette.mode),
      },
    },
    '& .odd': {
      backgroundColor: 'rgb(180, 200, 220)',
      '&:hover': {
        backgroundColor: getHoverBackgroundColor('rgb(180, 200, 220)', theme.palette.mode),
      },
    },
  }));

  const fontStylesChartTypography = {
    fontSize: '18px',
    textAlign: 'left',
    fontWeight: '800',
    fontFamily: 'DM Sans, sans-serif',
  };
  const textFieldLabelStyles = {
    style: {
      fontSize: '10px',
      color: 'rgb(26, 33, 56)',
      fontFamily: 'DM Sans, sans-serif',
    },
  };

  const columns = [
    {
      field: 'customerAccount',
      headerName: 'CUSTOMER ACCOUNT',
      width: 150,
      headerAlign: 'left',
      align: 'left',
      headerClassName: 'super-app-theme--header',
      flex: 1,
    },
    {
      field: 'driverId',
      headerName: 'DRIVER ID',
      width: 120,
      headerAlign: 'left',
      align: 'left',
      headerClassName: 'super-app-theme--header',
      flex: 1,
    },
    {
      field: 'driverName',
      headerName: 'DRIVER NAME',
      width: 150,
      headerAlign: 'left',
      align: 'left',
      headerClassName: 'super-app-theme--header',
      flex: 1,
    },
    {
      field: 'licenseNumber',
      headerName: 'LICENSE #',
      width: 150,
      headerAlign: 'left',
      align: 'left',
      headerClassName: 'super-app-theme--header',
      flex: 1,
    },
    {
      field: 'licenseState',
      headerName: 'LICENSE STATE',
      width: 120,
      headerAlign: 'left',
      align: 'left',
      headerClassName: 'super-app-theme--header',
      flex: 1,
    },
    {
      field: 'primaryPhone',
      headerName: 'PRIMARY PH #',
      width: 120,
      headerAlign: 'left',
      align: 'left',
      headerClassName: 'super-app-theme--header',
      flex: 1,
    },
    {
      field: 'secondaryPhone',
      headerName: 'SECONDARY PH #',
      width: 150,
      headerAlign: 'left',
      align: 'left',
      headerClassName: 'super-app-theme--header',
      flex: 1,
    },
    {
      field: 'unitNumber',
      headerName: 'UNIT #',
      width: 150,
      headerAlign: 'left',
      align: 'left',
      headerClassName: 'super-app-theme--header',
      flex: 1,
    },
    {
      field: 'driverStatus',
      headerName: 'STATUS',
      width: 100,
      headerAlign: 'left',
      align: 'left',
      headerClassName: 'super-app-theme--header',
      flex: 1,
    },
    {
      field: 'assignedCard',
      headerName: 'ASSIGNED CARD #',
      width: 150,
      headerAlign: 'left',
      align: 'left',
      headerClassName: 'super-app-theme--header',
      flex: 1,
    },
    {
      field: 'download',
      headerName: 'LICENSE DOC',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      headerClassName: 'super-app-theme--header',
      flex: 1,
      renderCell: params => (params.row.licenseFileName ? <DownloadIcon style={{ cursor: 'pointer', fontSize: '14px' }} onClick={() => handleDownload(params.row.licenseFileName)} color="primary" /> : 'N/A'),
    },
  ];

  const buttonsFontSize = '16px';
  const defaultButtonBackground = '#011f4b';
  const chosenButtonBackground = '#03396c';
  const inputLabelStyles = {
    fontSize: '10px',
    color: 'rgb(26, 33, 56)',
  };

  return (
    <Container maxWidth="lg" style={{ paddingTop: '20px' }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 'bold',
          fontFamily: 'sans-serif',
          fontSize: '14px',
          textTransform: 'uppercase',
          marginTop: '-8px',
        }}
      >
        Manage Drivers
      </Typography>

      {isSuccess && (
        <Alert severity="success" variant="filled" onClose={() => setIsSuccess(false)} sx={{ mb: 2, fontSize: '16px', textAlign: 'center' }}>
          <AlertTitle>Success</AlertTitle>
          {successMessage}
        </Alert>
      )}
      {isError && (
        <Alert severity="error" variant="filled" onClose={() => setIsError(false)} sx={{ mb: 2, fontSize: '16px', textAlign: 'center' }}>
          <AlertTitle>Error</AlertTitle>
          {errorMessage}
        </Alert>
      )}

      <Grid container spacing={6}>
        <Grid item xs={8}></Grid>
        <Grid item xs={4}>
          <Button
            style={{
              backgroundColor: '#31478F',
            }}
            variant="contained"
            sx={{
              mr: 2,
              mt: -5,
              ml: 26,
              borderRadius: '5px',
              padding: '6px 10px',
              whiteSpace: 'nowrap',
            }}
            onClick={async () => {
              setIsAddDriverSelected(true);
              setIsUpdateDriverSelected(false);
              resetFields();
              const response = await axios.get(`${BACKEND_URL}/v1/next/driver-id`);
              setDriverId(response.data);
            }}
          >
            <Typography sx={{ fontSize: '10px' }}>New Driver</Typography>
          </Button>
          <Button
            style={{
              backgroundColor: '#31478F',
            }}
            variant="contained"
            sx={{
              mr: 2,
              ml: -28,
              mt: -5,
              borderRadius: '5px',
              padding: '5px 10px',
            }}
            onClick={() => {
              setIsAddDriverSelected(false);
              setIsUpdateDriverSelected(true);
              resetFields();
            }}
          >
            <Typography sx={{ fontSize: '10px' }}>Update Driver</Typography>
          </Button>
        </Grid>
      </Grid>

      {isAddDriverSelected && (
        <form onSubmit={handleCreateDriver}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                label="Customer Account"
                fullWidth
                value={customerAccount}
                InputProps={{ readOnly: true }}
                InputLabelProps={{
                  sx: { fontSize: '11px' },
                }}
                sx={{
                  '& .MuiInputBase-root': { height: 25 },
                  '& .MuiInputBase-input': {
                    fontSize: 11,
                    padding: '4px',
                  },
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Driver ID"
                fullWidth
                value={driverId}
                onChange={e => setDriverId(e.target.value)}
                InputProps={{ readOnly: true }}
                required
                InputLabelProps={{
                  sx: { fontSize: '11px' },
                }}
                sx={{
                  '& .MuiInputBase-root': { height: 25 },
                  '& .MuiInputBase-input': {
                    fontSize: 11,
                    padding: '4px',
                  },
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Driver Name"
                fullWidth
                value={driverName}
                onChange={e => setDriverName(e.target.value)}
                InputLabelProps={{
                  sx: { fontSize: '11px', top: '-10px' },
                }}
                sx={{
                  '& .MuiInputBase-root': { height: 25 },
                  '& .MuiInputBase-input': {
                    fontSize: 11,
                    padding: '4px',
                  },
                }}
                required
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small" error={!!licenseNumberError}>
                <TextField
                  label="License #"
                  value={licenseNumber}
                  onChange={handleLicenseNumberChange}
                  InputLabelProps={{
                    sx: { fontSize: '11px', top: '-10px' },
                  }}
                  sx={{
                    '& .MuiInputBase-root': { height: 25 },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '4px',
                    },
                  }}
                  required
                  error={!!licenseNumberError}
                />
                {licenseNumberError && <FormHelperText>{licenseNumberError}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel style={inputLabelStyles}>License State</InputLabel>
                <Select
                  label="License State"
                  value={licenseState}
                  onChange={e => setLicenseState(e.target.value)}
                  InputLabelProps={{
                    sx: { fontSize: '11px', top: '-50%' },
                  }}
                  sx={{
                    '& .MuiInputBase-root': { height: 22, minHeight: 22 },
                    '& .MuiInputBase-input': { fontSize: 11, padding: '2px' },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Select a state</em>
                  </MenuItem>
                  {licenseStates.map(state => (
                    <MenuItem key={state.id} value={state.id}>
                      {state.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Primary Phone"
                fullWidth
                value={primaryPhone}
                onChange={e => {
                  const value = e.target.value;
                  if (value.startsWith('+1')) {
                    const digits = value.slice(2).replace(/\D/g, '');
                    if (digits.length <= 10) {
                      setPrimaryPhone('+1' + digits);
                    }
                  } else {
                    setPrimaryPhone('+1');
                  }
                }}
                inputProps={{
                  maxLength: 12,
                  pattern: '[+][1][0-9]{0,10}',
                }}
                required
                InputLabelProps={{
                  sx: { fontSize: '11px' },
                }}
                sx={{
                  '& .MuiInputBase-root': { height: 25 },
                  '& .MuiInputBase-input': {
                    fontSize: 11,
                    padding: '4px',
                  },
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Secondary Phone"
                fullWidth
                value={secondaryPhone}
                onChange={e => {
                  const value = e.target.value;
                  if (value.startsWith('+1')) {
                    const digits = value.slice(2).replace(/\D/g, '');
                    if (digits.length <= 10) {
                      setSecondaryPhone('+1' + digits);
                    }
                  } else {
                    setSecondaryPhone('+1');
                  }
                }}
                inputProps={{
                  maxLength: 12,
                  pattern: '[+][1][0-9]{0,10}',
                }}
                InputLabelProps={{
                  sx: { fontSize: '11px' },
                }}
                sx={{
                  '& .MuiInputBase-root': { height: 25 },
                  '& .MuiInputBase-input': {
                    fontSize: 11,
                    padding: '4px',
                  },
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel style={inputLabelStyles}>Unit #</InputLabel>
                <Select
                  label="Unit #"
                  value={unitNumber}
                  onChange={e => setUnitNumber(e.target.value)}
                  InputLabelProps={{
                    sx: { fontSize: '11px', top: '-50%' },
                  }}
                  sx={{
                    '& .MuiInputBase-root': { height: 22, minHeight: 22 },
                    '& .MuiInputBase-input': { fontSize: 11, padding: '2px' },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Select a unit</em>
                  </MenuItem>
                  {unitNumbers.map(unit => (
                    <MenuItem key={unit} value={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel style={inputLabelStyles}>Status</InputLabel>
                <Select
                  label="Status"
                  value={driverStatus}
                  onChange={e => setDriverStatus(e.target.value)}
                  InputLabelProps={{
                    sx: { fontSize: '11px' },
                  }}
                  sx={{
                    '& .MuiInputBase-root': { height: 22, minHeight: 22 },
                    '& .MuiInputBase-input': { fontSize: 11, padding: '2px' },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                >
                  {driverStatuses.map(s => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel style={inputLabelStyles}>Assigned Card #</InputLabel>
                <Select
                  label="Assigned Card #"
                  value={assignedCard}
                  onChange={e => setAssignedCard(e.target.value)}
                  InputLabelProps={{
                    sx: { fontSize: '11px', top: '-50%' },
                  }}
                  sx={{
                    '& .MuiInputBase-root': { height: 22, minHeight: 22 },
                    '& .MuiInputBase-input': { fontSize: 11, padding: '2px' },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Select a card</em>
                  </MenuItem>
                  {cardsForClient.map(card => (
                    <MenuItem key={card.cardNumber} value={card.cardNumber}>
                      {card.cardNumber}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <Box>
                <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleFileChange} style={{ fontSize: '10px' }} />
                {licenseFile && <Typography variant="body2">{licenseFile.name}</Typography>}
              </Box>
            </Grid>
          </Grid>
          <Box mt={2} textAlign="center">
            <Button
              style={{
                backgroundColor: '#31478F',
                color: 'white',
                height: '30px',
                fontSize: '10px',
              }}
              variant="contained"
              sx={{
                mr: 2,
                borderRadius: '5px',
                padding: '5px 10px',
                color: 'white',
              }}
              type="submit"
            >
              Create
            </Button>
          </Box>
        </form>
      )}

      {isUpdateDriverSelected && (
        <form onSubmit={handleUpdateDriver}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                label="Customer Account"
                fullWidth
                value={customerAccount}
                InputProps={{ readOnly: true }}
                InputLabelProps={{
                  sx: { fontSize: '11px' },
                }}
                sx={{
                  '& .MuiInputBase-root': { height: 25, minHeight: 25 },
                  '& .MuiInputBase-input': { fontSize: 11, padding: '2px' },
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel style={inputLabelStyles}>Driver ID</InputLabel>
                <Select
                  label="Driver ID"
                  value={driverId}
                  onChange={handleUpdateDriverSelectionChange}
                  InputLabelProps={{
                    sx: { fontSize: '11px' },
                  }}
                  sx={{
                    '& .MuiInputBase-root': { height: 20, minHeight: 20 },
                    '& .MuiInputBase-input': { fontSize: 11, padding: '2px' },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Select a driver</em>
                  </MenuItem>
                  {driversList.map(driver => (
                    <MenuItem key={driver.driverId} value={driver.driverId}>
                      {driver.driverId}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Driver Name"
                fullWidth
                value={driverName}
                onChange={e => setDriverName(e.target.value)}
                required
                InputLabelProps={{
                  sx: { fontSize: '11px', top: '-10px' },
                }}
                sx={{
                  '& .MuiInputBase-root': { height: 25 },
                  '& .MuiInputBase-input': { fontSize: 11, padding: '4px' },
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small" error={!!licenseNumberError}>
                <TextField
                  label="License #"
                  value={licenseNumber}
                  onChange={handleLicenseNumberChange}
                  required
                  error={!!licenseNumberError}
                  InputLabelProps={{
                    sx: { fontSize: '11px', top: '-10px' },
                  }}
                  sx={{
                    '& .MuiInputBase-root': { height: 25 },
                    '& .MuiInputBase-input': { fontSize: 11, padding: '4px' },
                  }}
                />
                {licenseNumberError && <FormHelperText>{licenseNumberError}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel style={inputLabelStyles}>License State</InputLabel>
                <Select
                  label="License State"
                  value={licenseState}
                  onChange={e => setLicenseState(e.target.value)}
                  InputLabelProps={{
                    sx: { fontSize: '11px', top: '-50%' },
                  }}
                  sx={{
                    '& .MuiInputBase-root': { height: 22, minHeight: 22 },
                    '& .MuiInputBase-input': { fontSize: 11, padding: '2px' },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Select a state</em>
                  </MenuItem>
                  {licenseStates.map(state => (
                    <MenuItem key={state.id} value={state.id}>
                      {state.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Primary Phone"
                fullWidth
                value={primaryPhone}
                onChange={e => {
                  const value = e.target.value;
                  if (value.startsWith('+1')) {
                    const digits = value.slice(2).replace(/\D/g, '');
                    if (digits.length <= 10) {
                      setPrimaryPhone('+1' + digits);
                    }
                  } else {
                    setPrimaryPhone('+1');
                  }
                }}
                inputProps={{
                  maxLength: 12,
                  pattern: '[+][1][0-9]{0,10}',
                }}
                required
                InputLabelProps={{
                  sx: { fontSize: '11px' },
                }}
                sx={{
                  '& .MuiInputBase-root': { height: 25 },
                  '& .MuiInputBase-input': { fontSize: 11, padding: '4px' },
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Secondary Phone"
                fullWidth
                value={secondaryPhone}
                onChange={e => {
                  const value = e.target.value;
                  if (value.startsWith('+1')) {
                    const digits = value.slice(2).replace(/\D/g, '');
                    if (digits.length <= 10) {
                      setSecondaryPhone('+1' + digits);
                    }
                  } else {
                    setSecondaryPhone('+1');
                  }
                }}
                inputProps={{
                  maxLength: 12,
                  pattern: '[+][1][0-9]{0,10}',
                }}
                InputLabelProps={{
                  sx: { fontSize: '11px' },
                }}
                sx={{
                  '& .MuiInputBase-root': { height: 25 },
                  '& .MuiInputBase-input': { fontSize: 11, padding: '4px' },
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel style={inputLabelStyles}>Unit #</InputLabel>
                <Select
                  label="Unit #"
                  value={unitNumber}
                  onChange={e => setUnitNumber(e.target.value)}
                  InputLabelProps={{
                    sx: { fontSize: '11px', top: '-50%' },
                  }}
                  sx={{
                    '& .MuiInputBase-root': { height: 22, minHeight: 22 },
                    '& .MuiInputBase-input': { fontSize: 11, padding: '2px' },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Select a unit</em>
                  </MenuItem>
                  {unitNumbers.map(unit => (
                    <MenuItem key={unit} value={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel style={inputLabelStyles}>Status</InputLabel>
                <Select
                  label="Status"
                  value={driverStatus}
                  onChange={e => setDriverStatus(e.target.value)}
                  InputLabelProps={{
                    sx: { fontSize: '11px', top: '-50%' },
                  }}
                  sx={{
                    '& .MuiInputBase-root': { height: 22, minHeight: 22 },
                    '& .MuiInputBase-input': { fontSize: 11, padding: '2px' },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                >
                  {driverStatuses.map(s => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel style={inputLabelStyles}>Assigned Card #</InputLabel>
                <Select
                  label="Assigned Card #"
                  value={assignedCard}
                  onChange={e => setAssignedCard(e.target.value)}
                  InputLabelProps={{
                    sx: { fontSize: '11px', top: '-50%' },
                  }}
                  sx={{
                    '& .MuiInputBase-root': { height: 22, minHeight: 22 },
                    '& .MuiInputBase-input': { fontSize: 11, padding: '2px' },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '200px',
                        minHeight: '100px',
                        '& .MuiMenuItem-root': {
                          fontSize: '10px',
                          marginLeft: '5px',
                          padding: '5px 15px',
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Select a card</em>
                  </MenuItem>
                  {cardsForClient.map(card => (
                    <MenuItem key={card.cardNumber} value={card.cardNumber}>
                      {card.cardNumber}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <Box>
                <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleFileChange} style={{ fontSize: '10px' }} />
                {licenseFile && <Typography variant="body2">{licenseFile.name}</Typography>}
              </Box>
            </Grid>
          </Grid>
          <Box mt={2} textAlign="center">
            <Button
              style={{
                backgroundColor: '#31478F',
                color: 'white',
                height: '30px',
                fontSize: '10px',
              }}
              variant="contained"
              sx={{
                mr: 2,
                borderRadius: '5px',
                padding: '5px 10px',
                color: 'white',
              }}
              type="submit"
              disabled={!driverId}
            >
              Update
            </Button>
          </Box>
        </form>
      )}

      <Box sx={{ marginTop: '30px' }}>
        {driversList.length > 0 ? (
          <StyledDataGrid
            rows={driversList}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            slots={{ toolbar: CustomToolbar }}
            getRowId={row => row.driverId}
            getRowClassName={params => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
            columnHeaderHeight={31}
            rowHeight={22}
            sx={{
              '& .super-app-theme--header': {
                backgroundColor: '#31478F !important',
                color: 'white',
                fontSize: '12px',
              },
              '& .MuiDataGrid-columnHeaders': {
                height: 25,
                fontSize: '12px',
              },
              '& .MuiDataGrid-cell': {
                padding: '2px 8px',
                fontSize: '10px',
              },
              '& .MuiDataGrid-iconButton': {
                fontSize: '12px',
                color: 'grey',
              },
              '& .MuiDataGrid-row': {
                minHeight: '28px',
              },
              '& .MuiDataGrid-row.even': {
                backgroundColor: '#F8F8F8',
              },
              '& .MuiDataGrid-row.odd': {
                backgroundColor: '#E6F0FF',
              },
              '@media (max-width: 768px)': {
                '& .super-app-theme--header': {
                  fontSize: '10px',
                },
                '& .MuiDataGrid-cell': {
                  padding: '2px 4px',
                  fontSize: '10px',
                },
                '& .MuiDataGrid-iconButton': {
                  fontSize: '10px',
                },
                '& .MuiDataGrid-row': {
                  minHeight: '26px',
                },
              },
            }}
          />
        ) : (
          <Typography>No drivers available.</Typography>
        )}
      </Box>
    </Container>
  );
};

export default DriverForm;
